import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { formatTicketDateTime } from './utils'
import { TicketPreviewEye } from './TicketQuickPreview'
import {
    approvalChip,
    assigneeLabel,
    inspectionProgress,
} from './ticket-inspection-utils'

// 2026-10-22 Tickets page "By inspection" view: the findings of one inspection under one card with its
// department / site, inspector, progress and report approval state.

const Group = styled.article`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    margin-bottom: 14px;
    overflow: hidden;
`

const Head = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 6px 16px;
    padding: 14px 16px;

    @media (max-width: 767px) {
        grid-template-columns: minmax(0, 1fr);
    }
`

const Toggle = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    background: none;
    border: 0;
    padding: 0;
    text-align: left;
    font-weight: 600;
    font-size: 15px;
    color: #111827;
    cursor: pointer;

    svg {
        flex: none;
        color: #6b7280;
        transition: transform 0.15s;
        transform: ${p => (p.$open ? 'none' : 'rotate(-90deg)')};
    }
    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`

const Meta = styled.div`
    grid-column: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    padding-left: 22px;
    font-size: 12.5px;
    color: #6b7280;

    .loc {
        color: #111827;
        font-weight: 500;
    }
`

const Right = styled.div`
    grid-column: 2;
    grid-row: 1 / span 3;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;

    @media (max-width: 767px) {
        grid-column: 1;
        grid-row: auto;
        align-items: flex-start;
        padding-left: 22px;
    }
`

const Progress = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: #6b7280;
    white-space: nowrap;

    .bar {
        width: 120px;
        height: 6px;
        background: #eef0f4;
        border-radius: 3px;
        overflow: hidden;
    }
    .bar i {
        display: block;
        height: 100%;
        background: #16a34a;
    }
`

const CHIP = {
    wait: { bg: '#fff3dc', color: '#a15c00' },
    sent: { bg: '#e0e7ff', color: '#3730a3' },
    ok: { bg: '#dcfce7', color: '#15803d' },
    bad: { bg: '#fee2e2', color: '#b91c1c' },
    muted: { bg: '#f1f5f9', color: '#475569' },
}

const Chip = styled.span`
    display: inline-block;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    background: ${p => (CHIP[p.$tone] || CHIP.muted).bg};
    color: ${p => (CHIP[p.$tone] || CHIP.muted).color};
`

const Links = styled.div`
    grid-column: 1;
    display: flex;
    gap: 14px;
    padding-left: 22px;
    font-size: 12.5px;

    a {
        color: #2563eb;
        font-weight: 500;
    }
`

const Rows = styled.div`
    border-top: 1px solid #e5e7eb;
`

const Row = styled.div`
    display: grid;
    grid-template-columns: 70px minmax(0, 1fr) 120px 150px 120px;
    gap: 12px;
    align-items: center;
    padding: 10px 16px 10px 38px;
    border-top: 1px solid #f1f2f6;
    cursor: pointer;

    &:first-child {
        border-top: 0;
    }
    &:hover,
    &:focus-visible {
        background: #f8fafc;
        outline: none;
    }
    .id {
        color: #2563eb;
        font-weight: 600;
        font-family: monospace;
    }
    .title {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }
    .title span {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }
    .small {
        color: #6b7280;
        font-size: 12.5px;
    }

    @media (max-width: 767px) {
        grid-template-columns: 60px minmax(0, 1fr);
        padding-left: 16px;
        .small,
        .status {
            grid-column: 2;
        }
    }
`

const Chevron = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
)

const InspectionGroup = ({
    group,
    locationLabel,
    assigneeNames,
    onOpen,
    onPreview,
    renderStatus,
}) => {
    const [open, setOpen] = useState(true)
    const s = group.summary
    const chip = approvalChip(s)
    const progress = inspectionProgress(group)
    const pct = progress.total
        ? Math.round((progress.done * 100) / progress.total)
        : 0
    const shownHere = group.tickets.length
    const bodyId = `insp-${group.auditId}`

    return (
        <Group>
            <Head>
                <Toggle
                    type="button"
                    $open={open}
                    aria-expanded={open}
                    aria-controls={bodyId}
                    onClick={() => setOpen(v => !v)}
                >
                    <Chevron />
                    <span>{group.name}</span>
                </Toggle>
                <Meta>
                    {s?.locationName ? (
                        <span className="loc">
                            {locationLabel}: {s.locationName}
                        </span>
                    ) : (
                        group.auditId > 0 && (
                            <span className="loc">No {locationLabel.toLowerCase()}</span>
                        )
                    )}
                    {s?.inspectorName && <span>Inspector: {s.inspectorName}</span>}
                    {s?.completedOn && (
                        <span>Completed {formatTicketDateTime(s.completedOn)}</span>
                    )}
                    {group.templateName && <span>{group.templateName}</span>}
                    <span>
                        {progress.total} finding{progress.total === 1 ? '' : 's'}
                        {progress.total > shownHere ? ` (${shownHere} in this list)` : ''}
                    </span>
                </Meta>
                <Right>
                    {progress.total > 0 && (
                        <Progress>
                            <div className="bar">
                                <i style={{ width: `${pct}%` }} />
                            </div>
                            {progress.done} of {progress.total} resolved
                        </Progress>
                    )}
                    {chip && <Chip $tone={chip.tone}>{chip.text}</Chip>}
                </Right>
                {s?.auditGuid && (
                    <Links>
                        {s.auditStatus === 5 ? (
                            <Link to={`/app/templates/inspection/report/${s.auditGuid}`}>
                                View report
                            </Link>
                        ) : (
                            <Link to={`/app/templates/inspection/${s.auditGuid}`}>
                                Open inspection
                            </Link>
                        )}
                    </Links>
                )}
            </Head>
            {open && (
                <Rows id={bodyId}>
                    {group.tickets.map(ticket => (
                        <Row
                            key={ticket.ticketId}
                            role="button"
                            tabIndex={0}
                            onClick={() => onOpen(ticket.ticketId)}
                            onKeyDown={event => {
                                // keys on the preview (eye) button belong to that button
                                if (event.target !== event.currentTarget) return
                                if (event.key !== 'Enter' && event.key !== ' ') return
                                event.preventDefault()
                                onOpen(ticket.ticketId)
                            }}
                        >
                            <span className="id">#{ticket.ticketId}</span>
                            <span className="title">
                                <span>{ticket.title || '—'}</span>
                                <TicketPreviewEye onClick={e => onPreview(e, ticket)} />
                            </span>
                            <span className="small">
                                {assigneeLabel(ticket, assigneeNames)}
                            </span>
                            <span className="small">
                                {formatTicketDateTime(ticket.createdOn)}
                            </span>
                            <span className="status">{renderStatus(ticket)}</span>
                        </Row>
                    ))}
                </Rows>
            )}
        </Group>
    )
}

export const TicketInspectionGroups = ({ groups, ...rest }) => (
    <div>
        {groups.map(group => (
            <InspectionGroup key={group.auditId} group={group} {...rest} />
        ))}
    </div>
)
